<template>
  <div class="charts-grid">
    <!-- Loads by Account -->
    <div style="display: contents;">
      <CardDashboard
        title="Loads by Account"
        type="graphCard"
        accent-color="#4F46E5"
        class="chart-card"
        :style="{ display: 'block', width: '100%', minWidth: '0', minHeight: '400px' }"
      >
        <template #header>
          <div class="top-accounts-header">
            <div class="top-accounts-title">Loads by Account</div>
            <div class="top-accounts-label">LAST 6 MONTHS</div>
          </div>
        </template>
        <AccountRankingList :items="formattedLoadsByAccount" :show-index="true" />
      </CardDashboard>
    </div>

    <!-- Revenue per Month -->
    <div style="display: contents;">
      <CardDashboard
        title="Revenue per month"
        type="graphCard"
        accent-color="#4F46E5"
        class="chart-card"
      >
        <template #header>
          <div class="revenue-header">
            <div class="revenue-title">Revenue per month</div>
            <div class="revenue-legend">
              <div v-for="item in revenueLegendItems" :key="item.name" class="legend-item">
                <div class="legend-color-line" :style="{ backgroundColor: item.color }"></div>
                <span class="legend-text">{{ item.shortName }}</span>
              </div>
              <span v-if="revenuePerMonth && revenuePerMonth.series && revenueLegendItems.length < revenuePerMonth.series.length" class="legend-more">
                +{{ revenuePerMonth.series.length - revenueLegendItems.length }} more</span>
            </div>
          </div>
        </template>
        <v-chart
          v-if="revenuePerMonth && revenuePerMonth.months && revenuePerMonth.months.length > 0"
          :style="{ width: '100%', height: '350px' }"
          :option="revenuePerMonthOption"
          autoresize
        />
      </CardDashboard>
    </div>

    <!-- Section Divider -->
    <div class="section-divider"></div>

    <!-- Active Accounts Widget -->
    <div style="display: contents;">
      <div class="active-accounts-widget">
        <SummarySection
          title="ACTIVE ACCOUNTS"
          :value="'10/10'"
          :variation="0"
          icon="fas fa-users"
          icon-color="#8B5CF6"
        />
      </div>
    </div>

    <!-- Top Accounts - Loads -->
    <div style="display: contents;">
      <CardDashboard
        title="Top accounts - loads"
        type="graphCard"
        accent-color="#E65100"
        class="chart-card"
      >
        <template #header>
          <div class="top-accounts-header">
            <div class="top-accounts-title">Top accounts - loads</div>
            <div class="top-accounts-label">RANKED</div>
          </div>
        </template>
        <AccountRankingList :items="formattedTopAccountsLoads" :show-index="true" />
      </CardDashboard>
    </div>

    <!-- Top Accounts - Revenue -->
    <div style="display: contents;">
      <CardDashboard
        title="Top accounts - revenue"
        type="graphCard"
        accent-color="#4A148C"
        class="chart-card"
      >
        <template #header>
          <div class="top-accounts-header">
            <div class="top-accounts-title">Top accounts - revenue</div>
            <div class="top-accounts-label">USD</div>
          </div>
        </template>
        <AccountRankingList :items="formattedTopAccountsRevenue" :show-index="true" />
      </CardDashboard>
    </div>
  </div>
</template>

<script>
import SummarySection from "./SummarySection.vue";
import CardDashboard from "../../components/CardDashboard.vue";
import AccountRankingList from "./AccountRankingList.vue";

export default {
  name: "ChartsSection",
  components: {
    SummarySection,
    CardDashboard,
    AccountRankingList,
  },
  props: {
    formattedLoadsByAccount: {
      type: Array,
      required: true,
    },
    revenueLegendItems: {
      type: Array,
      required: true,
    },
    revenuePerMonth: {
      type: Object,
      required: true,
    },
    revenuePerMonthOption: {
      type: Object,
      required: true,
    },
    formattedTopAccountsLoads: {
      type: Array,
      required: true,
    },
    formattedTopAccountsRevenue: {
      type: Array,
      required: true,
    },
  },
};
</script>

<style lang="scss" scoped>
@use "@/assets/scss/_themes" as *;
@use "@/assets/scss/_variables" as *;
@use "@/assets/scss/_dashboard-mixins" as *;
@use "@/assets/scss/_mixins" as *;

// charts-grid debe estar al nivel principal, no anidado
.charts-grid {
  display: grid !important;
  grid-template-columns: repeat(2, 1fr) !important;
  gap: 24px !important;
  width: 100% !important;
  height: auto !important;

  .section-divider {
    grid-column: span 2;
    height: 1px;
    background-color: #e5e7eb;
    width: 100%;
    margin: 30px 0;
  }

  .active-accounts-widget {
    grid-column: span 2;
    display: flex;
    justify-content: flex-start;
    margin-bottom: 20px;

    .summary-card {
      max-width: 400px;
      width: 50%;
    }
  }

  .chart-card {
    @include chart-card;

    h3 {
      margin-bottom: 20px;
      color: #1f2937;
      font-size: 16px;
    }

    .chart {
      height: 320px;
      width: 100%;
      :deep(.echarts-tooltip) {
        color: #1f2937 !important;
      }
      :deep(.echarts-legend) {
        color: #374151 !important;
      }
      :deep(.echarts-axis-label) {
        color: #6b7280 !important;
      }
      :deep(.echarts-axis-line) {
        color: #e5e7eb !important;
      }
    }
    .revenue-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      padding: 0;

      .revenue-title {
        margin: 0;
        color: #1f2937;
        font-size: 18px;
        font-weight: 600;
      }

      .revenue-legend {
        display: flex;
        align-items: center;
        gap: 20px;
        flex-wrap: wrap;

        .legend-item {
          display: flex;
          align-items: center;
          gap: 8px;

          .legend-color-line {
            width: 16px;
            height: 4px;
            border-radius: 2px;
            flex-shrink: 0;
          }

          .legend-text {
            font-size: 14px;
            color: #6b7280;
            font-weight: 500;
            white-space: nowrap;
          }
        }

        .legend-more {
          font-size: 14px;
          color: #9ca3af;
          font-weight: 500;
        }
      }
    }
    .top-accounts-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      padding: 0;

      .top-accounts-title {
        margin: 0;
        color: #1f2937;
        font-size: 18px;
        font-weight: 600;
      }

      .top-accounts-label {
        font-size: 12px;
        color: #9ca3af;
        font-weight: 500;
        letter-spacing: 0.5px;
        text-transform: uppercase;
      }
    }
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .charts-grid {
    display: flex !important;
    flex-direction: column !important;
    grid-template-columns: 1fr !important;
    gap: 16px !important;
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    box-sizing: border-box;

    .section-divider {
      grid-column: span 1 !important;
      margin: 20px 0;
      width: 100% !important;
      max-width: 100% !important;
      box-sizing: border-box;
    }

    .active-accounts-widget {
      grid-column: span 1 !important;
      margin-bottom: 16px;
      width: 100% !important;
      max-width: 100% !important;
      min-width: 0 !important;
      box-sizing: border-box;

      .summary-card {
        max-width: 100% !important;
        width: 100% !important;
        min-width: 0 !important;
        box-sizing: border-box;
      }
    }

    .chart-card {
      height: auto !important;
      min-height: 350px;
      width: 100% !important;
      max-width: 100% !important;
      min-width: 0 !important;
      box-sizing: border-box;

      .chart {
        height: 300px !important;
        min-height: 300px;
        width: 100% !important;
        max-width: 100% !important;
        overflow: hidden;
        box-sizing: border-box;
      }

      .revenue-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
        width: 100% !important;
        max-width: 100% !important;
        box-sizing: border-box;

        .revenue-legend {
          gap: 12px;
          max-width: 100%;
          overflow-x: auto;
          padding-bottom: 4px;

          .legend-item {
            .legend-text {
              font-size: 12px;
            }
          }

          .legend-more {
            font-size: 12px;
          }
        }
      }

      .top-accounts-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
        width: 100% !important;
        max-width: 100% !important;
        box-sizing: border-box;

        .top-accounts-title {
          font-size: 16px;
        }

        .top-accounts-label {
          font-size: 11px;
        }
      }
    }
  }
}

@media (min-width: 769px) and (max-width: 1023px) {
  .charts-grid {
    grid-template-columns: repeat(2, 1fr) !important;
    gap: 16px !important;

    .section-divider {
      grid-column: span 2 !important;
    }

    .active-accounts-widget {
      grid-column: span 2 !important;

      .summary-card {
        max-width: 100% !important;
        width: 100% !important;
      }
    }

    .chart-card {
      .revenue-header {
        .revenue-legend {
          gap: 16px;
          flex-wrap: wrap;

          .legend-text {
            font-size: 13px;
          }
        }
      }
    }
  }
}

@media (min-width: 1024px) and (max-width: 1384px) {
  .charts-grid {
    grid-template-columns: 1fr !important;
    gap: 16px !important;

    .section-divider {
      grid-column: span 1 !important;
    }

    .active-accounts-widget {
      grid-column: span 1 !important;

      .summary-card {
        max-width: 100% !important;
        width: 100% !important;
      }
    }
  }
}
</style>
